<template>
  <div class="modal fade" :id="modalId" tabindex="-1" role="dialog" aria-labelledby="monthlyBillingLabel" aria-hidden="true">
    <div class="modal-dialog modal-lg modal-dialog-centered" role="document">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title" id="monthlyBillingLabel">Monthly Rent Billing</h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>
        <div class="modal-body">
          <!-- Year Selection -->
          <div class="row mb-3">
            <div class="col-md-6">
              <label class="form-label">Select Year</label>
              <select v-model="selectedYear" @change="loadBillingHistory" class="form-select">
                <option v-for="year in availableYears" :key="year" :value="year">{{ year }}</option>
              </select>
            </div>
          </div>

          <!-- Unbilled Months -->
          <div v-if="unbilledMonths.length > 0" class="mb-4">
            <h6>Unbilled Months</h6>
            <div class="alert alert-warning">
              <i class="mdi mdi-alert-circle-outline me-2"></i>
              The following months have not been billed yet. Click to run billing:
            </div>
            <div class="row">
              <div v-for="month in unbilledMonths" :key="`${month.month}-${month.year}`" class="col-md-3 mb-2">
                <button @click="runBilling(month.month, month.year)" class="btn btn-primary w-100">
                  <i class="mdi mdi-calculator me-1"></i>
                  {{ month.monthName }} {{ month.year }}
                </button>
              </div>
            </div>
          </div>

          <!-- Billing History -->
          <div class="mt-4">
            <h6>Billing History for {{ selectedYear }}</h6>
            <div class="table-responsive">
              <table class="table table-bordered table-sm">
                <thead class="table-light">
                <tr>
                  <th>Month</th>
                  <th>Run Date</th>
                  <th>Run By</th>
                  <th>Units Billed</th>
                  <th>Total Amount</th>
                  <th>Status</th>
                </tr>
                </thead>
                <tbody>
                <tr v-if="billingHistory.length === 0">
                  <td colspan="6" class="text-center text-muted">No billing history for this year</td>
                </tr>
                <tr v-for="bill in billingHistory" :key="bill.id">
                  <td>{{ getMonthName(bill.month) }}</td>
                  <td>{{ bill.formatted_date }}</td>
                  <td>{{ bill.run_by }}</td>
                  <td>{{ bill.units_billed }}</td>
                  <td>KES {{ numberFormat(bill.total_amount_billed) }}</td>
                  <td>
                      <span class="badge" :class="bill.status === 'COMPLETED' ? 'bg-success' : 'bg-warning'">
                        {{ bill.status }}
                      </span>
                  </td>
                </tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- Loading State -->
          <div v-if="loading" class="text-center py-4">
            <div class="spinner-border text-primary" role="status">
              <span class="sr-only">Loading...</span>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Close</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { execute, rent } from "@/api";

export default {
  name: "MonthlyBillingModal",
  props: {
    modalId: {
      type: String,
      default: "monthly-billing-modal"
    }
  },
  data() {
    return {
      selectedYear: new Date().getFullYear(),
      availableYears: [],
      billingHistory: [],
      unbilledMonths: [],
      loading: false
    };
  },
  mounted() {
    // Generate available years (current year and previous 2 years)
    const currentYear = new Date().getFullYear();
    for (let i = 0; i < 3; i++) {
      this.availableYears.push(currentYear - i);
    }
  },
  methods: {
    loadBillingHistory() {
      this.loading = true;
      const data = new FormData();
      data.append("function", "getBillingHistory");
      data.append("year", this.selectedYear);

      execute(data, rent)
          .then((res) => {
            this.loading = false;
            if (res.data.success) {
              this.billingHistory = res.data.data.billingHistory || [];
              this.unbilledMonths = res.data.data.unbilledMonths || [];
            }
          })
          .catch(() => {
            this.loading = false;
            alert("Failed to load billing history");
          });
    },

    runBilling(month, year) {
      const monthName = this.getMonthName(month);

      if (!confirm(`Are you sure you want to run billing for ${monthName} ${year}? This will add rent to arrears for all occupied units.`)) {
        return;
      }

      this.loading = true;
      const data = new FormData();
      data.append("function", "runMonthlyBilling");
      data.append("month", month);
      data.append("year", year);
      data.append("sessionNames", sessionStorage.getItem("sessionNames"));
      data.append("sessionIdNo", sessionStorage.getItem("sessionIdNo"));

      execute(data, rent)
          .then((res) => {
            this.loading = false;
            if (res.data.success) {
              alert(res.data.message);
              this.$emit('billing-completed', res.data.data);
              this.loadBillingHistory();
            } else {
              alert(res.data.message);
            }
          })
          .catch((e) => {
            this.loading = false;
            alert("Error: " + e.message);
          });
    },

    getMonthName(monthNumber) {
      const months = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
      ];
      return months[monthNumber - 1];
    },

    numberFormat(num) {
      const formatter = new Intl.NumberFormat("en-GB");
      return formatter.format(num || 0);
    }
  }
};
</script>

<style scoped>
.table th {
  font-weight: 600;
}
</style>